import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { sanitizeReturnUrl } from '@core/utils/url-sanitizer';

/**
 * Guard that prevents authenticated users from accessing the login page.
 * If a user is already authenticated, they will be redirected to their
 * role-appropriate default page or the returnUrl if provided.
 */
export const loginGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // If there's a message query param (access-denied, logout, etc.), always allow login page
  // This prevents loops when redirecting due to auth failures
  if (route.queryParams['message'] || route.queryParams['logout']) {
    return true;
  }

  // Check if user is already authenticated
  const isAuthenticated = authService.isAuthenticated();

  if (!isAuthenticated) {
    // User is not authenticated, allow access to login page
    return true;
  }

  // User is authenticated, redirect them away from login page
  let userData;
  try {
    userData = await authService.getUserData();
  } catch (error) {
    console.error('Error fetching user data in login guard:', error);
    // Allow access to login page if we can't verify user data
    return true;
  }

  if (!userData) {
    // If we can't get user data despite being authenticated, allow login page
    // This handles edge cases where auth state is inconsistent
    return true;
  }

  // Check for returnUrl in query parameters
  const returnUrl = route.queryParams['returnUrl'];
  const sanitizedReturnUrl = sanitizeReturnUrl(returnUrl);

  if (sanitizedReturnUrl) {
    // Redirect to the return URL if provided
    // Use parseUrl to properly handle multi-segment paths
    return router.parseUrl(sanitizedReturnUrl);
  }

  // Redirect to root, which will use role-redirect component to determine destination
  return router.createUrlTree(['/']);
};
